import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, X, Check, ArrowRight, FileImage } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { SEO } from '../components/SEO';
import { useLanguageStore } from '../store';
import { useTranslation } from '../i18n/translations';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { getRecaptchaToken } from '../lib/recaptcha';
import { trackEvent } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const labels = {
  en: {
    title: 'Custom Furniture',
    subtitle: 'Fill out a short questionnaire so our manager can prepare a personalized proposal for you.',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    phone: 'Phone',
    roomDimensions: 'Room/space dimensions where the furniture will be placed',
    roomDimensionsPlaceholder: 'e.g. 3.5m x 2.8m x 2.7m (length x width x height)',
    roomPhotos: 'Photos of the room/space (if available)',
    furnitureWishes: 'Furniture wishes: type, color, style',
    furnitureWishesPlaceholder: 'Describe what you want: type of furniture, preferred color, materials, style...',
    furniturePhotos: 'Reference photos or sketches (if available)',
    budget: 'Budget (EUR)',
    budgetPlaceholder: 'How much are you planning to invest in this product?',
    submit: 'SEND QUESTIONNAIRE',
    uploading: 'Uploading...',
    success: 'Thank you! Your questionnaire has been received. Our manager will prepare a proposal and contact you within 24 hours.',
    dragDrop: 'Click or drag files here',
    maxSize: 'JPG, PNG, PDF up to 10MB',
  },
  nl: {
    title: 'Maatwerk Meubels',
    subtitle: 'Vul een korte vragenlijst in zodat onze manager een gepersonaliseerd voorstel voor u kan voorbereiden.',
    firstName: 'Voornaam', lastName: 'Achternaam',
    email: 'E-mail', phone: 'Telefoon',
    roomDimensions: 'Afmetingen ruimte waar het meubel komt',
    roomDimensionsPlaceholder: 'bijv. 3.5m x 2.8m x 2.7m',
    roomPhotos: "Foto's van de ruimte (indien beschikbaar)",
    furnitureWishes: 'Meubelwensen: type, kleur, stijl',
    furnitureWishesPlaceholder: 'Beschrijf wat u wilt: type, kleur, materialen, stijl...',
    furniturePhotos: "Referentiefoto's of schetsen (indien beschikbaar)",
    budget: 'Budget (EUR)',
    budgetPlaceholder: 'Hoeveel wilt u investeren?',
    submit: 'VRAGENLIJST VERZENDEN', uploading: 'Uploaden...',
    success: 'Bedankt! Uw vragenlijst is ontvangen. Onze manager neemt binnen 24 uur contact met u op.',
    dragDrop: 'Klik of sleep bestanden hier', maxSize: 'JPG, PNG, PDF tot 10MB',
  },
  ro: {
    title: 'Mobilier la Comanda',
    subtitle: 'Completati un scurt chestionar pentru ca managerul nostru sa va poata pregati o oferta personalizata.',
    firstName: 'Prenume', lastName: 'Nume',
    email: 'Email', phone: 'Telefon',
    roomDimensions: 'Dimensiunile spatiului unde va fi mobilierul',
    roomDimensionsPlaceholder: 'ex. 3.5m x 2.8m x 2.7m',
    roomPhotos: 'Fotografii spatiu (daca aveti)',
    furnitureWishes: 'Dorinte mobilier: tip, culoare, stil',
    furnitureWishesPlaceholder: 'Descrieti ce doriti: tipul, culoarea, materialele, stilul...',
    furniturePhotos: 'Fotografii de referinta sau schite (daca aveti)',
    budget: 'Buget (EUR)',
    budgetPlaceholder: 'Cat doriti sa investiti?',
    submit: 'TRIMITE CHESTIONAR', uploading: 'Se incarca...',
    success: 'Multumim! Chestionarul a fost trimis. Managerul nostru va va contacta in 24 de ore.',
    dragDrop: 'Click sau trage fisierele aici', maxSize: 'JPG, PNG, PDF pana la 10MB',
  },
  ru: {
    title: 'Мебель на заказ',
    subtitle: 'Заполните короткую анкету, чтобы наш менеджер мог подготовить для вас персональное предложение.',
    firstName: 'Имя',
    lastName: 'Фамилия',
    email: 'Email',
    phone: 'Телефон',
    roomDimensions: 'Размеры помещения/места где будет мебель',
    roomDimensionsPlaceholder: 'напр. 3.5м x 2.8м x 2.7м (длина x ширина x высота)',
    roomPhotos: 'Фото помещения (если есть)',
    furnitureWishes: 'Пожелания по мебели: вид, цвет, стиль',
    furnitureWishesPlaceholder: 'Опишите что хотите: тип мебели, цвет, материалы, стиль...',
    furniturePhotos: 'Фото-референсы или эскизы (если есть)',
    budget: 'Бюджет (EUR)',
    budgetPlaceholder: 'Сколько планируете вложить в данный продукт?',
    submit: 'ОТПРАВИТЬ АНКЕТУ',
    uploading: 'Загрузка...',
    success: 'Спасибо! Ваша анкет получен. Наш менеджер подготовит предложение и свяжется с вами в течение 24 часов.',
    dragDrop: 'Нажмите или перетащите файлы сюда',
    maxSize: 'JPG, PNG, PDF до 10МБ',
  },
};

const FileUploadArea = ({ files, setFiles, label, id, l }) => {
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (fileList) => {
    const incoming = Array.from(fileList || []).filter(Boolean);
    if (incoming.length === 0) return;
    setUploading(true);

    const results = await Promise.all(
      incoming.map(async (file) => {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name}: file too large (max 10MB)`);
          return null;
        }
        try {
          const formData = new FormData();
          formData.append('file', file);
          const res = await fetch(`${API}/api/upload`, { method: 'POST', body: formData });
          if (!res.ok) {
            let detail = '';
            try { detail = (await res.json())?.detail || ''; } catch (_) { /* non-JSON error */ }
            throw new Error(detail || `Server error ${res.status}`);
          }
          const data = await res.json();
          return { file_id: data.file_id, name: file.name, path: data.url || data.path };
        } catch (err) {
          toast.error(`Failed to upload ${file.name}${err?.message ? ` — ${err.message}` : ''}`);
          return null;
        }
      })
    );

    setFiles((prev) => [...prev, ...results.filter(Boolean)]);
    setUploading(false);
  };

  return (
    <div>
      <Label className="text-muted-foreground">{label}</Label>
      <div
        className="mt-2 border-2 border-dashed border-border p-6 text-center cursor-pointer hover:border-foreground/40 transition-colors"
        onClick={() => document.getElementById(id).click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        data-testid={`upload-${id}`}
      >
        <input
          id={id}
          type="file"
          multiple
          accept="image/*,.pdf"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <div className="w-5 h-5 border-2 border-foreground/30 border-t-foreground rounded-full animate-spin" />
            <span className="text-sm">{l.uploading}</span>
          </div>
        ) : (
          <>
            <Upload className="w-6 h-6 mx-auto mb-2 text-muted-foreground" strokeWidth={1.5} />
            <p className="text-sm text-muted-foreground">{l.dragDrop}</p>
            <p className="text-xs text-muted-foreground mt-1">{l.maxSize}</p>
          </>
        )}
      </div>
      {files.length > 0 && (
        <div className="mt-3 space-y-2">
          {files.map((f, idx) => (
            <div key={f.file_id || `${f.name}-${idx}`} className="flex items-center gap-3 p-2 bg-muted text-sm">
              <FileImage className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="flex-1 truncate">{f.name}</span>
              <button onClick={() => setFiles((prev) => prev.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-destructive"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const CustomFurniturePage = () => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const l = labels[language] || labels.en;

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [briefId, setBriefId] = useState('');
  const [furnitureFiles, setFurnitureFiles] = useState([]);
  const [roomFiles, setRoomFiles] = useState([]);
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    room_dimensions: '', furniture_description: '', budget: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const recaptcha_token = await getRecaptchaToken('lead_form_completed');
      const payload = {
        name: `${formData.first_name} ${formData.last_name}`.trim(),
        email: formData.email,
        phone: formData.phone,
        room_dimensions: formData.room_dimensions,
        furniture_description: formData.furniture_description,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        furniture_images: furnitureFiles.map((f) => f.file_id),
        room_images: roomFiles.map((f) => f.file_id),
        building_type: 'not_specified',
        wishes: '',
        recaptcha_token,
      };
      const res = await fetch(`${API}/api/custom-furniture/brief`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed');
      const result = await res.json();
      trackEvent('lead_form_completed', { source: 'custom_furniture_brief' });
      setBriefId(result.brief_id);
      setSubmitted(true);
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="brief-success">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-lg px-6">
          <div className="w-20 h-20 bg-foreground text-background rounded-full flex items-center justify-center mx-auto mb-8">
            <Check className="w-10 h-10" />
          </div>
          <h1 className="text-h2 mb-4">{l.success}</h1>
          <p className="text-muted-foreground mb-2 text-sm">ID: {briefId}</p>
          <Link to={`/${language}`}>
            <Button className="btn-primary mt-6">
              {t?.common?.ok || 'OK'}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="custom-furniture-page">
      <SEO title={l.title} path="custom-furniture" />
      <div className="bg-surface section-padding">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
            <p className="text-caption text-muted-foreground mb-4">{t.categories?.custom_furniture || 'Custom Furniture'}</p>
            <h1 className="text-h1 mb-4">{l.title}</h1>
            <p className="text-body-lg text-muted-foreground">{l.subtitle}</p>
          </motion.div>
        </div>
      </div>

      <div className="container-custom py-16">
        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-8">
          {/* Step 1: Contact Info */}
          <div className="space-y-1">
            <h3 className="text-sm font-medium tracking-widest text-muted-foreground uppercase">01</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>{l.firstName} *</Label>
              <Input name="first_name" value={formData.first_name} onChange={handleChange} className="h-12 mt-2" required data-testid="brief-first-name" />
            </div>
            <div>
              <Label>{l.lastName} *</Label>
              <Input name="last_name" value={formData.last_name} onChange={handleChange} className="h-12 mt-2" required data-testid="brief-last-name" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>{l.phone} *</Label>
              <Input name="phone" type="tel" value={formData.phone} onChange={handleChange} className="h-12 mt-2" required data-testid="brief-phone" />
            </div>
            <div>
              <Label>{l.email} *</Label>
              <Input name="email" type="email" value={formData.email} onChange={handleChange} className="h-12 mt-2" required data-testid="brief-email" />
            </div>
          </div>

          <div className="border-t border-border" />

          {/* Step 2: Room */}
          <div className="space-y-1">
            <h3 className="text-sm font-medium tracking-widest text-muted-foreground uppercase">02</h3>
          </div>
          <div>
            <Label>{l.roomDimensions} *</Label>
            <Input name="room_dimensions" value={formData.room_dimensions} onChange={handleChange} className="h-12 mt-2" placeholder={l.roomDimensionsPlaceholder} required data-testid="brief-dimensions" />
          </div>
          <FileUploadArea files={roomFiles} setFiles={setRoomFiles} label={l.roomPhotos} id="room-photos" l={l} />

          <div className="border-t border-border" />

          {/* Step 3: Furniture wishes */}
          <div className="space-y-1">
            <h3 className="text-sm font-medium tracking-widest text-muted-foreground uppercase">03</h3>
          </div>
          <div>
            <Label>{l.furnitureWishes} *</Label>
            <Textarea name="furniture_description" value={formData.furniture_description} onChange={handleChange} className="mt-2" rows={4} placeholder={l.furnitureWishesPlaceholder} required data-testid="brief-description" />
          </div>
          <FileUploadArea files={furnitureFiles} setFiles={setFurnitureFiles} label={l.furniturePhotos} id="furniture-photos" l={l} />

          <div className="border-t border-border" />

          {/* Step 4: Budget */}
          <div className="space-y-1">
            <h3 className="text-sm font-medium tracking-widest text-muted-foreground uppercase">04</h3>
          </div>
          <div>
            <Label>{l.budget}</Label>
            <Input name="budget" type="number" value={formData.budget} onChange={handleChange} className="h-12 mt-2" placeholder={l.budgetPlaceholder} data-testid="brief-budget" />
          </div>

          <Button type="submit" className="btn-primary w-full text-base py-3" disabled={loading} data-testid="submit-brief">
            {loading ? l.uploading : l.submit}
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CustomFurniturePage;
